import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import DiseaseActivityHistoryApi from 'api/DiseaseActivityHistoryApi';
import DiseaseApi from 'api/DiseaseApi';
import { DiseaseActivityHistoryLoadCount, Geoname } from 'utils/constants';
import { getDateStrToday } from 'utils/dateTimeHelpers';
import { FuncReturnTypeSync, PayloadOf } from 'utils/typeHelpers';

import diseaseActivitiesTimeFrameSelectors from 'diseaseActivitiesTimeFrame/diseaseActivitiesTimeFrame.selectors';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';
import { DiseaseActivityParams } from 'diseaseActivityPanels/diseaseActivityPanels.slice';

import {
  loadDiseaseActivityUpdatesError,
  loadDiseaseActivityUpdatesStart,
  loadDiseaseActivityUpdatesSuccess,
  loadNewerDiseaseActivityUpdatesError,
  loadNewerDiseaseActivityUpdatesStart,
  loadNewerDiseaseActivityUpdatesSuccess,
  loadOlderDiseaseActivityUpdatesError,
  loadOlderDiseaseActivityUpdatesStart,
  loadOlderDiseaseActivityUpdatesSuccess,
  setTopDescriptionForSelectedActivityCountry,
  setTopDescriptionForSelectedUserLocation,
} from './diseaseActivityUpdates.slice';

export type SelectDiseaseActivityUpdatesSagaPayload = {
  activityDate?: string;
  excludeCaseCounts: boolean;
};

export const filterDiseaseActivityUpdates = createAction<SelectDiseaseActivityUpdatesSagaPayload>(
  'diseaseActivityUpdates/filterDiseaseActivityUpdates'
);
export const fetchDiseaseActivityUpdates = createAction<SelectDiseaseActivityUpdatesSagaPayload>(
  'diseaseActivityUpdates/fetchDiseaseActivityUpdates'
);
export const fetchOlderDiseaseActivityUpdates = createAction<SelectDiseaseActivityUpdatesSagaPayload>(
  'diseaseActivityUpdates/fetchOlderDiseaseActivityUpdates'
);
export const fetchNewerDiseaseActivityUpdates = createAction<SelectDiseaseActivityUpdatesSagaPayload>(
  'diseaseActivityUpdates/fetchNewerDiseaseActivityUpdates'
);
export const fetchTopDescriptionForUserLocation = createAction(
  'diseaseActivityUpdates/fetchTopDescription'
);
export const fetchTopDescriptionForCountry = createAction(
  'diseaseActivityUpdates/fetchTopDescriptionForCountry'
);

type DailyActivityType =
  | FuncReturnTypeSync<typeof DiseaseActivityHistoryApi.GetGlobalDiseaseActivityUpdates>
  | FuncReturnTypeSync<typeof DiseaseActivityHistoryApi.GetCountryDiseaseActivityUpdates>
  | FuncReturnTypeSync<typeof DiseaseActivityHistoryApi.GetNewerGlobalDiseaseActivityUpdates>
  | FuncReturnTypeSync<typeof DiseaseActivityHistoryApi.GetNewerCountryDiseaseActivityUpdates>
  | FuncReturnTypeSync<typeof DiseaseActivityHistoryApi.GetOlderGlobalDiseaseActivityUpdates>
  | FuncReturnTypeSync<typeof DiseaseActivityHistoryApi.GetOlderCountryDiseaseActivityUpdates>
  | FuncReturnTypeSync<typeof DiseaseActivityHistoryApi.GetHierarchicalDiseaseActivityUpdates>
  | FuncReturnTypeSync<typeof DiseaseActivityHistoryApi.GetNewerHierarchicalDiseaseActivityUpdates>
  | FuncReturnTypeSync<typeof DiseaseActivityHistoryApi.GetOlderHierarchicalDiseaseActivityUpdates>;

function fetchDiseaseActivityUpdatesSaga() {
  return function* (action: PayloadAction<SelectDiseaseActivityUpdatesSagaPayload>) {
    const disacParams: DiseaseActivityParams = yield select(
      diseaseActivityPanelsSelectors.disacParams
    );
    const { userLocationId, diseaseId, speciesId, countryId, activityDate } = disacParams;

    if (!diseaseId || !speciesId) {
      return;
    }

    try {
      yield put(loadDiseaseActivityUpdatesStart());

      type TApiPayload =
        | PayloadOf<typeof DiseaseActivityHistoryApi.GetCountryDiseaseActivityUpdates>
        | PayloadOf<typeof DiseaseActivityHistoryApi.GetHierarchicalDiseaseActivityUpdates>
        | PayloadOf<typeof DiseaseActivityHistoryApi.GetGlobalDiseaseActivityUpdates>;
      const apiPayload: TApiPayload = {
        diseaseId: diseaseId,
        speciesId: speciesId,
        activityDate: action.payload.activityDate || activityDate || getDateStrToday(),
        excludeCaseCounts: action.payload.excludeCaseCounts,
        offset:
          action.payload.activityDate || activityDate
            ? DiseaseActivityHistoryLoadCount.deepLinkDatePad
            : DiseaseActivityHistoryLoadCount.initOrMoreLoads,
      };
      const response: DailyActivityType = countryId
        ? yield call(DiseaseActivityHistoryApi.GetCountryDiseaseActivityUpdates, {
            ...apiPayload,
            countryId: countryId,
          })
        : userLocationId
        ? yield call(DiseaseActivityHistoryApi.GetHierarchicalDiseaseActivityUpdates, {
            ...apiPayload,
            locationId: userLocationId,
          })
        : yield call(DiseaseActivityHistoryApi.GetGlobalDiseaseActivityUpdates, apiPayload);
      yield put(loadDiseaseActivityUpdatesSuccess(response));
    } catch (e) {
      if (e?.response?.status === 404) {
        yield put(loadDiseaseActivityUpdatesSuccess({}));
      } else {
        yield put(loadDiseaseActivityUpdatesError());
      }
    }
  };
}

function fetchOlderDiseaseActivityUpdatesSaga() {
  return function* (action: PayloadAction<SelectDiseaseActivityUpdatesSagaPayload>) {
    const disacParams: DiseaseActivityParams = yield select(
      diseaseActivityPanelsSelectors.disacParams
    );
    const { userLocationId, diseaseId, speciesId, countryId } = disacParams;

    try {
      yield put(loadOlderDiseaseActivityUpdatesStart());

      type TApiPayload =
        | PayloadOf<typeof DiseaseActivityHistoryApi.GetOlderCountryDiseaseActivityUpdates>
        | PayloadOf<typeof DiseaseActivityHistoryApi.GetOlderHierarchicalDiseaseActivityUpdates>
        | PayloadOf<typeof DiseaseActivityHistoryApi.GetOlderGlobalDiseaseActivityUpdates>;
      const apiPayload: TApiPayload = {
        diseaseId: diseaseId,
        speciesId: speciesId,
        activityDate: action.payload.activityDate || getDateStrToday(),
        count: DiseaseActivityHistoryLoadCount.initOrMoreLoads,
        excludeCaseCounts: action.payload.excludeCaseCounts,
      };
      const response: DailyActivityType = countryId
        ? yield call(DiseaseActivityHistoryApi.GetOlderCountryDiseaseActivityUpdates, {
            ...apiPayload,
            countryId: countryId,
          })
        : userLocationId
        ? yield call(DiseaseActivityHistoryApi.GetOlderHierarchicalDiseaseActivityUpdates, {
            ...apiPayload,
            locationId: userLocationId,
          })
        : yield call(DiseaseActivityHistoryApi.GetOlderGlobalDiseaseActivityUpdates, apiPayload);

      yield put(loadOlderDiseaseActivityUpdatesSuccess(response));
    } catch (e) {
      if (e?.response?.status === 404) {
        yield put(loadOlderDiseaseActivityUpdatesSuccess({}));
      } else {
        yield put(loadOlderDiseaseActivityUpdatesError());
      }
    }
  };
}

function fetchNewerDiseaseActivityUpdatesSaga() {
  return function* (action: PayloadAction<SelectDiseaseActivityUpdatesSagaPayload>) {
    const disacParams: DiseaseActivityParams = yield select(
      diseaseActivityPanelsSelectors.disacParams
    );
    const { userLocationId, diseaseId, speciesId, countryId } = disacParams;

    try {
      yield put(loadNewerDiseaseActivityUpdatesStart());

      type TApiPayload =
        | PayloadOf<typeof DiseaseActivityHistoryApi.GetNewerCountryDiseaseActivityUpdates>
        | PayloadOf<typeof DiseaseActivityHistoryApi.GetNewerHierarchicalDiseaseActivityUpdates>
        | PayloadOf<typeof DiseaseActivityHistoryApi.GetNewerGlobalDiseaseActivityUpdates>;
      const apiPayload: TApiPayload = {
        diseaseId: diseaseId,
        speciesId: speciesId,
        activityDate: action.payload.activityDate || getDateStrToday(),
        count: DiseaseActivityHistoryLoadCount.initOrMoreLoads,
        excludeCaseCounts: action.payload.excludeCaseCounts,
      };
      const response: DailyActivityType = countryId
        ? yield call(DiseaseActivityHistoryApi.GetNewerCountryDiseaseActivityUpdates, {
            ...apiPayload,
            countryId: countryId,
          })
        : userLocationId
        ? yield call(DiseaseActivityHistoryApi.GetNewerHierarchicalDiseaseActivityUpdates, {
            ...apiPayload,
            locationId: userLocationId,
          })
        : yield call(DiseaseActivityHistoryApi.GetNewerGlobalDiseaseActivityUpdates, apiPayload);

      yield put(loadNewerDiseaseActivityUpdatesSuccess(response));
    } catch (e) {
      if (e?.response?.status === 404) {
        yield put(loadNewerDiseaseActivityUpdatesSuccess({}));
      } else {
        yield put(loadNewerDiseaseActivityUpdatesError());
      }
    }
  };
}

function fetchTopDescriptionForUserLocationSaga() {
  return function* () {
    const disacParams: DiseaseActivityParams = yield select(
      diseaseActivityPanelsSelectors.disacParams
    );
    const { userLocationId, diseaseId, speciesId } = disacParams;
    const startDate: string = yield select(diseaseActivitiesTimeFrameSelectors.getStartDate);

    try {
      yield put(setTopDescriptionForSelectedUserLocation(null));
      const apiFunc = DiseaseApi.DiseaseController_GetDiseaseActivityDescriptions;
      const response: FuncReturnTypeSync<typeof apiFunc> = yield call(apiFunc, {
        locationId: userLocationId === Geoname.GLOBAL_VIEW ? null : userLocationId,
        diseaseId: diseaseId,
        speciesId: speciesId,
        startDate: startDate,
        count: 1,
      });
      yield put(setTopDescriptionForSelectedUserLocation(response));
    } catch (e) {
      yield put(setTopDescriptionForSelectedUserLocation(null));
    }
  };
}

function fetchTopDescriptionForCountrySaga() {
  return function* () {
    const disacParams: DiseaseActivityParams = yield select(
      diseaseActivityPanelsSelectors.disacParams
    );
    const { countryId, diseaseId, speciesId } = disacParams;
    const startDate: string = yield select(diseaseActivitiesTimeFrameSelectors.getStartDate);

    try {
      yield put(setTopDescriptionForSelectedActivityCountry(null));
      const apiFunc = DiseaseApi.DiseaseController_GetDiseaseActivityDescriptions;
      const response: FuncReturnTypeSync<typeof apiFunc> = yield call(apiFunc, {
        locationId: countryId,
        diseaseId: diseaseId,
        speciesId: speciesId,
        startDate: startDate,
        count: 1,
      });
      yield put(setTopDescriptionForSelectedActivityCountry(response));
    } catch (e) {
      yield put(setTopDescriptionForSelectedActivityCountry(null));
    }
  };
}

function* diseaseActivityUpdatesSaga() {
  yield takeLatest(fetchDiseaseActivityUpdates, fetchDiseaseActivityUpdatesSaga());
  yield takeLatest(fetchOlderDiseaseActivityUpdates, fetchOlderDiseaseActivityUpdatesSaga());
  yield takeLatest(fetchNewerDiseaseActivityUpdates, fetchNewerDiseaseActivityUpdatesSaga());
  yield takeLatest(fetchTopDescriptionForUserLocation, fetchTopDescriptionForUserLocationSaga());
  yield takeLatest(fetchTopDescriptionForCountry, fetchTopDescriptionForCountrySaga());
}

export default diseaseActivityUpdatesSaga;
